// packages/core/src/learning/schools/coast-fi-slow-fi.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'paula-pant',
// 'tanja-hester', 'vicki-robin', 'jl-collins' (all valid FigureIds) — wire
// into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const COAST_FI_SLOW_FI_EXHIBIT: SchoolExhibitEntry = {
	id: 'coast-fi-slow-fi',
	tagline:
		'Coast and slow FI believes freedom can arrive before full retirement.',
	worldviewSummary:
		'Coast and slow FI is paying attention to the point where compounding starts carrying more of the future load. This lens protects you from thinking financial independence only matters once you can quit work completely. It accepts a tradeoff: slowing down may mean a longer timeline, less maximum wealth, and more need for honest planning. The blind spot is comfort drift. A person can call something slow FI while avoiding the hard numbers, the savings habit, or the work needed to make the plan real.',
	coreBeliefs: [
		'Coast and slow FI believes the pressure can drop before the finish line.',
		'Coast and slow FI believes compounding can eventually carry more of the retirement load if enough is invested early enough.',
		'Coast and slow FI believes freedom is not only quitting work. It can mean changing work, lowering hours, taking better risks, or buying breathing room.',
		'Coast and slow FI believes a sustainable life can beat a burned out sprint.',
		'Coast and slow FI believes the point is not to worship the spreadsheet. The point is to design a life that keeps working.',
		'Coast and slow FI believes time, health, relationships, and energy belong in the money conversation.',
	],
	warnsAgainst: [
		'Treating FIRE as all or nothing.',
		'Sprinting so hard toward freedom that life becomes smaller now.',
		'Using coast FI language to avoid saving, planning, or facing the real numbers.',
		'Assuming future compounding will fix every weak habit or unstable income problem.',
	],
	helps: 'This lens helps people who want more freedom without turning life into a race to quit work. It may not fit someone who needs fast debt repair, urgent stability, or a plan that depends on aggressive saving for a short period.',
	figures: [],
	canon: [
		{
			title: 'Coast FI',
			oneLineIdea:
				'Once enough is invested for the future, new income may only need to cover current life, depending on assumptions.',
		},
		{
			title: 'Slow FI',
			oneLineIdea:
				'The path to freedom can include a better life along the way, not only a finish line decades away.',
		},
		{
			title: 'Your Money or Your Life',
			author: 'Vicki Robin and Joe Dominguez',
			oneLineIdea:
				'Money decisions are also life energy decisions, not just account balance decisions.',
		},
		{
			title: 'The Simple Path to Wealth',
			author: 'JL Collins',
			oneLineIdea:
				'Simple investing and time can do heavy lifting when the plan is consistent.',
		},
		{
			title: 'Work optional thinking',
			oneLineIdea:
				'The goal is not always never working again. Sometimes it is having enough options to choose better work.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-coast-fi-slow-fi',
	debateIds: [],
	applyActions: [
		{
			label: 'See what your savings rate does to your freedom date',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Build a freedom plan around lower pressure',
			action: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
		},
		{
			label: 'Check how much runway changes your options',
			action: { type: 'calculateRunway', label: 'Check your runway' },
		},
		{
			label: 'Write what enough would change before chasing more',
			action: {
				type: 'noToolAction',
				label: 'Define enough',
				prompt: 'Write what would change in your life if the pressure to save for the future dropped by half.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Coast FI asks a calmer question: when does the pressure start dropping?',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of rushing to freedom is building a life you cannot stand.',
			kind: 'hook',
		},
		{
			line: 'What would you change if you knew your future retirement was mostly on track?',
			kind: 'interview-question',
		},
		{
			line: 'Where does the FIRE movement help people, and where does it make life too narrow?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users test freedom as pressure reduction, not just a quit work fantasy.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'The app teaches users to see years, runway, and options before life forces the math.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience why financial freedom can begin before full retirement.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone what they would stop tolerating if money pressure dropped.',
			kind: 'creator-prompt',
		},
		{
			line: 'Walk through how a smaller future obligation can make today’s career choices less desperate.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
